import type {
  SearchPagination,
  SearchParamsObject,
  SearchResponse,
  SearchSingleIndexProps,
} from '@algolia/client-search';
import { searchClient } from '@algolia/client-search';
import queryString from 'query-string';

import { postJson } from '#/utils/http/postJson';
import { invariant } from '#/utils/invariant';
import { RichError } from '#/utils/RichError';
import { trackError } from '#/utils/trackError';

import { AlgoliaGeneralError } from './AlgoliaGeneralError';
import {
  AlgoliaValidUntilError,
  isValidUntilError,
} from './AlgoliaValidUntilError';

export type AlgoliaSearchArgs = {
  config: Readonly<{ appId: string; apiKey: string }>;
  query: string;
  indexName: string;
  options: SearchParamsObject;
  analytics?: boolean;
  onApiKeyTimeout?: () => Promise<string>;
  isUsingAlgoliaMock: boolean;
};

export async function algoliaSearch<TResponse>({
  config: { appId, apiKey },
  indexName,
  query,
  options,
  analytics = true,
  onApiKeyTimeout,
  isUsingAlgoliaMock,
}: AlgoliaSearchArgs): Promise<
  SearchResponse<TResponse> & Required<SearchPagination>
> {
  if (isUsingAlgoliaMock) {
    return postJson<SearchResponse<TResponse> & Required<SearchPagination>>(
      `http://algoliamock:6577/${indexName}/search`,
      {
        query,
        hitsPerPage: options.hitsPerPage,
        page: options.page,
        facets: options.facets,
        filters: options.filters,
        attributesToHighlight: options.attributesToHighlight,
      },
      {
        headers: {
          'x-algolia-api-key': apiKey,
        },
      },
    );
  }
  invariant(appId, 'Search app id must be configured');
  invariant(apiKey, 'Search api key must be configured');

  const params: SearchSingleIndexProps = {
    indexName,
    searchParams: {
      ...options,
      query: query ? query.trim() : '',
      getRankingInfo: true,
      clickAnalytics: true,
      analytics,
    },
  };

  const client = searchClient(appId, apiKey);

  try {
    const result = await client.searchSingleIndex<TResponse>(params);
    return result as SearchResponse<TResponse> & Required<SearchPagination>;
  } catch (e) {
    if (!(e instanceof Error)) throw e;

    if (isValidUntilError(e)) {
      if (onApiKeyTimeout) {
        const newAppKey = await onApiKeyTimeout();
        return algoliaSearch({
          config: { appId, apiKey: newAppKey },
          query,
          indexName,
          options,
          analytics,
          onApiKeyTimeout,
          isUsingAlgoliaMock: false,
        });
      }

      throw new AlgoliaValidUntilError();
    }

    const requestInfo = {
      errorMessage: e.message,
      requestParams: queryString.stringify(params),
      statusCode: 'statusCode' in e ? e.statusCode : undefined,
      response: 'response' in e ? e.response : undefined,
    };

    trackError(
      new RichError('Algolia POST request error.', {
        requestInfo: JSON.stringify(requestInfo),
      }),
    );

    throw new AlgoliaGeneralError(
      `Algolia Error: ${JSON.stringify(requestInfo)}`,
    );
  }
}
